import React, { useMemo, useCallback, ChangeEvent } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { CriterionConfig, useParticipantFieldsByName } from '@app/api';
import { Input, Slider, Toggle } from '@app/form';
import { FoldableOpenMore } from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.marginBase * 4,
  },
  toggle: {
    marginBottom: 0,
    marginTop: 0,
  },
  content: {
    marginTop: theme.marginBase,
    backgroundColor: theme.colors.white,
    padding: theme.marginBase,
    borderRadius: theme.borderRadius.std,
  },
}));

interface Props {
  name: string;
  value: CriterionConfig;
  onChange: (name: string, value: CriterionConfig) => any;
}

export const CriterionConfigFields = ({ name, value, onChange }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const participantFields = useParticipantFieldsByName();

  const title = useMemo(() => {
    const field = participantFields[name];
    return field ? field.label : t(`participants.display.${name}`);
  }, [participantFields, name]);

  const onEnabledChange = useCallback(
    (checked: boolean) => onChange(name, { ...value, enabled: checked }),
    [onChange, name, value],
  );

  const onAllowEmptyChange = useCallback(
    (checked: boolean) => onChange(name, { ...value, allowEmpty: checked }),
    [onChange, name, value],
  );

  const onEmptyMalusChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      onChange(name, { ...value, emptyMalus: Number(e.target.value) }),
    [name, onChange, value],
  );

  return (
    <div className={classes.container}>
      <Toggle
        label={title}
        checked={value.enabled}
        onChange={onEnabledChange}
      />
      <FoldableOpenMore label="Advanced">
        <div className={classes.content}>
          <Toggle
            label="Autoriser les valeurs vides"
            checked={value.allowEmpty}
            onChange={onAllowEmptyChange}
            containerClass={classes.toggle}
          />
          <Input
            label="Malus si vide"
            value={value.emptyMalus}
            onChange={onEmptyMalusChange}
            small
            type="number"
            step={0.1}
            min={0}
            max={1}
          />
        </div>
      </FoldableOpenMore>
    </div>
  );
};
